import { forwardRef, useContext } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/utils/cn';
import type { SelectProps } from './Select.types';
import { tokens } from '@/theme/tokens';
import { ThemeContext } from '@/context/ThemeContext';

/**
 * # Select
 *
 * Zakladni vyber z moznosti (native select).
 * Podporuje varianty, velikosti a validacni stavy.
 *
 * @example
 * <Select label="Role" options={[{ value: 'admin', label: 'Admin' }]} />
 */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  (
    {
      options,
      label,
      error,
      hint,
      size = 'md',
      placeholder,
      fullWidth = true,
      className,
      id,
      required,
      disabled,
      'data-testid': testId,
      ...nativeProps
    },
    ref,
  ) => {
    // Generovany ID pro label-select propojeni
    const selectId = id || `select-${label?.toLowerCase().replace(/\s+/g, '-')}`;
    const hasError = !!error;
    const theme = useContext(ThemeContext);
    const isDark = theme?.mode === 'dark';
    const themedForegroundClass = isDark ? 'ui-field-foreground-dark' : 'ui-field-foreground-light';
    const defaultThemeClasses = isDark
      ? 'border-white/10 bg-zinc-950/72 text-zinc-100'
      : 'border-emerald-100/80 bg-white/92 text-gray-900';
    const errorThemeClasses = isDark
      ? 'border-red-400 bg-red-950/25 text-zinc-100'
      : 'border-red-500 bg-red-50/90 text-gray-900';
    const disabledThemeClasses = isDark ? 'bg-zinc-800/50' : 'bg-gray-50';

    return (
      <div className={cn('flex flex-col gap-1.5', fullWidth && 'w-full')}>
        {/* Label */}
        {label && (
          <label
            htmlFor={selectId}
            className={cn(
              'text-sm font-medium',
              'text-gray-700 dark:text-zinc-200',
              disabled && 'opacity-50',
            )}
          >
            {label}
            {required && <span className="text-red-500 ml-1">*</span>}
          </label>
        )}

        {/* Select wrapper */}
        <div className="relative">
          <select
            ref={ref}
            id={selectId}
            disabled={disabled}
            aria-describedby={
              hasError ? `${selectId}-error` : hint ? `${selectId}-hint` : undefined
            }
            data-testid={testId}
            {...(hasError ? { 'aria-invalid': true } : {})}
            className={cn(
              // Base
              'w-full appearance-none border outline-none shadow-sm',
              tokens.transition.fast,
              themedForegroundClass,
              tokens.radius.sm,
              // Size
              sizeClasses[size],
              // Padding for chevron
              'pr-10',
              // State
              hasError ? errorThemeClasses : defaultThemeClasses,
              // Disabled
              disabled && 'opacity-50 cursor-not-allowed',
              disabled && disabledThemeClasses,
              className,
            )}
            {...nativeProps}
          >
            {placeholder && (
              <option value="" disabled selected>
                {placeholder}
              </option>
            )}
            {options.map((option) => (
              <option key={option.value} value={option.value} disabled={option.disabled}>
                {option.label}
              </option>
            ))}
          </select>

          {/* Chevron Icon */}
          <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-500 dark:text-zinc-400">
            <ChevronDown size={16} />
          </div>
        </div>

        {/* Error / Hint zprava */}
        {hasError && (
          <p
            id={`${selectId}-error`}
            className="text-sm text-red-500 dark:text-red-400"
            role="alert"
          >
            {error}
          </p>
        )}
        {!hasError && hint && (
          <p id={`${selectId}-hint`} className="text-sm text-gray-500 dark:text-zinc-400">
            {hint}
          </p>
        )}
      </div>
    );
  },
);

Select.displayName = 'Select';

// --- Style mapy ---

const sizeClasses: Record<string, string> = {
  sm: 'px-3 py-1.5 text-sm',
  md: 'px-3.5 py-2 text-sm',
  lg: 'px-4 py-2.5 text-base',
};

